// src/features/incidents/incidentsService.js
import { getData, saveData } from '../../services/storageService';

// Статуси інцидентів
export const INCIDENT_STATUS = {
  OPEN: 'open',
  UNDER_REVIEW: 'under_review',
  IN_PROGRESS: 'in_progress',
  WAITING_FOR_APPROVAL: 'waiting_for_approval',
  RESOLVED: 'resolved',
  CLOSED: 'closed',
  REOPENED: 'reopened'
};

// Пріоритети інцидентів
export const INCIDENT_PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

export const getIncidents = async () => {
  try {
    const incidents = await getData('lainsa.incidents');
    return Array.isArray(incidents) ? incidents : [];
  } catch (error) {
    console.error('Error getting incidents:', error);
    return [];
  }
};

export const saveIncidents = async (incidents) => {
  try {
    await saveData('lainsa.incidents', incidents);
    return true;
  } catch (error) {
    console.error('Error saving incidents:', error);
    return false;
  }
};

// ПОЛІПШЕНА функція для відправки сповіщень
const sendIncidentNotification = async (incident, type, user, additionalData = {}) => {
  try {
    const notificationActions = window.notificationActions;
    
    if (!notificationActions) {
      console.warn('Notification system not available');
      return;
    }

    let title = '';
    let message = '';
    let priority = 'info';
    let actions = ['view'];
    let category = 'incidents';

    switch (type) {
      case 'created':
        title = '🆕 Новий інцидент';
        message = `Створено інцидент типу "${incident.type}" для ${incident.clientName}`;
        priority = incident.priority === 'critical' ? 'high' : 'medium';
        actions = ['view', 'assign'];
        break;
        
      case 'response_added':
        title = '💬 Відповідь додана';
        message = `${user?.name || 'Користувач'} додав(ла) відповідь до інциденту`;
        priority = 'medium';
        actions = ['view', 'reply'];
        break;
        
      case 'status_changed':
        title = '🔄 Статус змінено';
        message = `Інцидент переведено в статус "${getStatusText(incident.status)}"`;
        priority = incident.priority === 'critical' ? 'high' : 'medium';
        actions = ['view', 'history'];
        break;
        
      case 'priority_escalated':
        title = '🚨 Підвищено пріоритет';
        message = `Пріоритет інциденту змінено на "${getPriorityText(incident.priority)}"`;
        priority = 'high';
        actions = ['view', 'acknowledge'];
        break;

      case 'assigned':
        title = '👤 Інцидент призначено';
        message = `Інцидент призначено для ${additionalData.assignee}`;
        priority = 'medium';
        actions = ['view', 'accept'];
        break;

      case 'due_soon':
        title = '⏰ Термін рішення близький';
        message = `Термін вирішення інциденту закінчується через ${additionalData.hours} годин`;
        priority = 'high';
        actions = ['view', 'extend'];
        break;

      case 'iot_created':
        title = '📡 IoT інцидент';
        message = `Автоматично створено інцидент з даних датчика: ${additionalData.sensorType}`;
        priority = 'high';
        actions = ['view', 'acknowledge'];
        category = 'iot_alerts';
        break;

      case 'image_analysis_complete':
        title = '🖼️ Аналіз фото завершено';
        message = `Проаналізовано ${additionalData.photosCount} фото. Виявлено ${additionalData.anomaliesCount} аномалій.`;
        priority = 'medium';
        actions = ['view_report', 'export'];
        category = 'image_analysis';
        break;

      case 'image_analysis_started':
        title = '🔍 Початок аналізу фото';
        message = `Запущено AI аналіз ${additionalData.photosCount} фото інциденту`;
        priority = 'info';
        actions = ['view_progress'];
        category = 'image_analysis';
        break;

      case 'maintenance_reminder':
        title = '🔧 Нагадування про ТО';
        message = `Заплановане обслуговування: ${additionalData.equipmentName}`;
        priority = 'medium';
        actions = ['view_schedule', 'postpone'];
        category = 'maintenance';
        break;
    }

    // Відправка сповіщення
    notificationActions.addNotification({
      type: type,
      priority: priority,
      title: title,
      message: message,
      data: {
        incident: incident,
        user: user,
        ...additionalData
      },
      actions: actions,
      category: category,
      timestamp: new Date()
    });

    // Логування для debug
    console.log(`📢 Sent notification: ${type} for incident ${incident.id}`);

  } catch (error) {
    console.error('❌ Error sending notification:', error);
  }
};

// Допоміжні функції для текстів
function getStatusText(status) {
  const statusMap = {
    open: 'Відкрито',
    under_review: 'На розгляді',
    in_progress: 'В роботі',
    waiting_for_approval: 'Очікує підтвердження',
    resolved: 'Вирішено',
    closed: 'Закрито',
    reopened: 'Перевідкрито'
  };
  return statusMap[status] || status;
}

function getPriorityText(priority) {
  const priorityMap = {
    low: 'Низький',
    medium: 'Середній',
    high: 'Високий',
    critical: 'Критичний'
  };
  return priorityMap[priority] || priority;
}

function isPriorityEscalated(oldPriority, newPriority) {
  const priorityLevels = {
    low: 1,
    medium: 2,
    high: 3,
    critical: 4
  };
  
  return priorityLevels[newPriority] > priorityLevels[oldPriority];
}

function generateId() {
  return 'inc_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
}

// ОСНОВНІ ФУНКЦІї
export const addIncident = async (incident) => {
  const incidents = await getIncidents();
  const newIncident = {
    id: generateId(),
    createdAt: new Date().toISOString(),
    status: INCIDENT_STATUS.OPEN,
    priority: INCIDENT_PRIORITY.MEDIUM,
    responses: [],
    ...incident
  };
  
  incidents.unshift(newIncident);
  await saveIncidents(incidents);
  
  // Відправляємо сповіщення про створення інциденту
  await sendIncidentNotification(newIncident, 'created', incident.createdBy);
  
  return newIncident;
};

export const updateIncident = async (id, updates) => {
  const incidents = await getIncidents();
  const index = incidents.findIndex(inc => inc.id === id);
  
  if (index !== -1) {
    const oldIncident = incidents[index];
    incidents[index] = { ...incidents[index], ...updates, updatedAt: new Date().toISOString() };
    await saveIncidents(incidents);
    
    const updatedIncident = incidents[index];
    
    // Сповіщення про зміну статусу
    if (updates.status && updates.status !== oldIncident.status) {
      await sendIncidentNotification(updatedIncident, 'status_changed', 
        updates.updatedBy || oldIncident.createdBy, {
          oldStatus: oldIncident.status,
          newStatus: updates.status
        });
    }
    
    // Сповіщення про зміну пріоритету
    if (updates.priority && updates.priority !== oldIncident.priority) {
      await sendIncidentNotification(updatedIncident, 'priority_escalated',
        updates.updatedBy || oldIncident.createdBy, {
          oldPriority: oldIncident.priority,
          newPriority: updates.priority
        });
    }
    
    return updatedIncident;
  }
  return null;
};

export const addResponse = async (incidentId, response, user) => {
  const incidents = await getIncidents();
  const index = incidents.findIndex(inc => inc.id === incidentId);
  
  if (index !== -1) {
    const newResponse = {
      id: generateId(),
      message: response.message,
      status: response.status,
      priority: response.priority,
      createdAt: new Date().toISOString(),
      createdBy: {
        id: user.id,
        name: user.username || user.name,
        role: user.role
      }
    };
    
    if (!incidents[index].responses) {
      incidents[index].responses = [];
    }
    
    incidents[index].responses.push(newResponse);
    
    // Перевіряємо зміни статусу та пріоритету
    const statusChanged = incidents[index].status !== response.status;
    const priorityChanged = incidents[index].priority !== response.priority;
    
    incidents[index].status = response.status;
    incidents[index].priority = response.priority;
    incidents[index].updatedAt = new Date().toISOString();
    
    await saveIncidents(incidents);
    
    // Сповіщення про додавання відповіді
    await sendIncidentNotification(incidents[index], 'response_added', user, {
      responseId: newResponse.id
    });
    
    // Додаткові сповіщення про зміни
    if (statusChanged) {
      await sendIncidentNotification(incidents[index], 'status_changed', user);
    }
    
    if (priorityChanged && isPriorityEscalated(incidents[index].priority, response.priority)) {
      await sendIncidentNotification(incidents[index], 'priority_escalated', user);
    }
    
    return incidents[index];
  }
  return null;
};

export const deleteIncident = async (id) => {
  const incidents = await getIncidents();
  const filtered = incidents.filter(inc => inc.id !== id);
  await saveIncidents(filtered);
  return true;
};

export const getIncident = async (id) => {
  const incidents = await getIncidents();
  return incidents.find(inc => inc.id === id) || null;
};

// НОВІ ФУНКЦІЇ ДЛЯ IoT ТА COMPUTER VISION

export const addIoTIncident = async (incidentData) => {
  const incidents = await getIncidents();
  const newIncident = {
    id: 'iot_' + generateId(),
    createdAt: new Date().toISOString(),
    status: INCIDENT_STATUS.OPEN,
    priority: INCIDENT_PRIORITY.HIGH,
    responses: [],
    isAutoCreated: true,
    source: 'iot',
    ...incidentData
  };
  
  incidents.unshift(newIncident);
  await saveIncidents(incidents);
  
  // Спеціальне сповіщення для IoT інцидентів
  await sendIncidentNotification(newIncident, 'iot_created', 
    { name: 'IoT System', role: 'system' }, {
      sensorType: incidentData.iotData?.sensorType,
      machineId: incidentData.iotData?.machineId,
      sensorValue: incidentData.iotData?.sensorValue
    });
  
  return newIncident;
};

export const updateIncidentWithImageAnalysis = async (incidentId, analysisResults) => {
  const incidents = await getIncidents();
  const index = incidents.findIndex(inc => inc.id === incidentId);
  
  if (index !== -1) {
    incidents[index] = {
      ...incidents[index],
      imageAnalysis: analysisResults.analyses,
      lastAnalysis: new Date().toISOString(),
      analysisSummary: analysisResults.summary,
      updatedAt: new Date().toISOString()
    };
    
    await saveIncidents(incidents);
    
    // Сповіщення про завершення аналізу
    await sendIncidentNotification(incidents[index], 'image_analysis_complete', 
      { name: 'AI System', role: 'system' }, {
        photosCount: analysisResults.analyses.length,
        anomaliesCount: analysisResults.summary?.totalAnomalies || 0,
        analysisId: analysisResults.analysisId
      });
    
    return incidents[index];
  }
  return null;
};

export const notifyImageAnalysisStarted = async (incidentId, photosCount) => {
  const incident = await getIncident(incidentId);
  if (incident) {
    await sendIncidentNotification(incident, 'image_analysis_started', 
      { name: 'AI System', role: 'system' }, {
        photosCount: photosCount
      });
  }
};

// ДОДАТКОВІ СЛУЖБОВІ ФУНКЦІЇ

export const getIncidentsByStatus = async (status) => {
  const incidents = await getIncidents();
  return incidents.filter(inc => inc.status === status);
};

export const getIncidentsByPriority = async (priority) => {
  const incidents = await getIncidents();
  return incidents.filter(inc => inc.priority === priority);
};

export const getRecentIncidents = async (days = 7) => {
  const incidents = await getIncidents();
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - days);
  
  return incidents.filter(inc => new Date(inc.createdAt) > cutoffDate);
};

export const getIncidentsWithPhotos = async () => {
  const incidents = await getIncidents();
  return incidents.filter(inc => inc.photos && inc.photos.length > 0);
};

export const searchIncidents = async (query) => {
  const incidents = await getIncidents();
  const searchTerm = query.toLowerCase();
  
  return incidents.filter(inc => 
    inc.clientName?.toLowerCase().includes(searchTerm) ||
    inc.type?.toLowerCase().includes(searchTerm) ||
    inc.description?.toLowerCase().includes(searchTerm) ||
    inc.responsible?.toLowerCase().includes(searchTerm)
  );
};